/**
 * Success view shown after creating an org, inviting a member, or issuing a
 * reset. Three flavors:
 *  - One-time password (Cloud Identity provider) → show the hosted username
 *    + temporary password for out-of-band relay, plus the sign-in URL.
 *  - Setup / reset link → render the copyable link.
 *  - Nothing to hand over → the account is already active; tell ops the
 *    person can just sign in.
 */
import { useState } from 'react';

const PORTAL_URL = 'https://cs-portal-dev-478221.web.app';

function useCopy() {
  const [copied, setCopied] = useState(false);
  async function copy(text, fallbackId) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = fallbackId && document.getElementById(fallbackId);
      if (el) { el.select(); document.execCommand('copy'); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return [copied, copy];
}

// emailSent: true = the onboarding email (link, steps, pictures) went to the
// person; false = it did not (email not set up here, or ops chose not to);
// undefined = this flavor never emails (a one-time password, a reset link).
export default function SigninLinkPanel({
  email, setupLink, firebaseUserCreated, hasPassword, onDone,
  identityEmail, oneTimePassword, authProvider, emailSent,
}) {
  const [copied, copy] = useCopy();
  const [copiedOtp, copyOtp] = useCopy();
  const isGoogle = authProvider === 'google_identity';
  const signinHome = isGoogle ? `${window.location.origin}/login` : PORTAL_URL;

  if (oneTimePassword) {
    const relay = `Username: ${identityEmail}\nTemporary password: ${oneTimePassword}\nSign in: ${setupLink}`;
    return (
      <div className="signin-link-panel">
        <div className="signin-link-success">
          <h3>{hasPassword ? 'Temporary password issued' : 'Sign-in details issued'}</h3>
          <p>
            Relay these to <strong>{email}</strong> out of band (phone / in person — not
            email). They sign in with the username below; Google forces a new password and
            authenticator setup on first sign-in, which retires this temporary password.
          </p>
        </div>
        <div className="info-grid">
          <div className="info-item"><label>Username</label><span className="mono">{identityEmail}</span></div>
          <div className="info-item"><label>Temporary password</label><span className="mono">{oneTimePassword}</span></div>
          <div className="info-item"><label>Sign-in URL</label><span className="mono">{setupLink}</span></div>
        </div>
        <div className="button-row">
          <button type="button" className="btn btn-primary" onClick={() => copyOtp(relay)}>
            {copiedOtp ? 'Copied ✓' : 'Copy all'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onDone}>Done</button>
        </div>
        <p className="signin-link-hint">
          Shown once. If it's lost before they sign in, issue another from <strong>Account →
          Send password reset</strong>.
        </p>
      </div>
    );
  }

  if (!setupLink) {
    return (
      <div className="signin-link-panel">
        <div className="signin-link-success">
          <h3>Member added — no link needed</h3>
          <p>
            <strong>{email}</strong> already has an active ChargeStack account.
            Tell them they've been added to this org. Next time they sign in,
            they'll see it.
          </p>
        </div>
        <div className="signin-link-row">
          <input
            id="setup-link-input"
            className="form-input signin-link-input"
            readOnly
            value={signinHome}
            onFocus={e => e.target.select()}
          />
          <button type="button" className="btn btn-primary" onClick={() => copy(signinHome, 'setup-link-input')}>
            {copied ? 'Copied ✓' : 'Copy URL'}
          </button>
        </div>
        <p className="signin-link-hint">
          If they need a password reset for any reason, use <strong>Account →
          Send password reset</strong> on their member row.
        </p>
        <div className="button-row">
          <button type="button" className="btn btn-ghost" onClick={onDone}>Done</button>
        </div>
      </div>
    );
  }

  const heading = emailSent
    ? 'Invitation emailed'
    : firebaseUserCreated
      ? 'User provisioned'
      : (hasPassword ? 'Password reset link generated' : 'Setup link generated');

  return (
    <div className="signin-link-panel">
      <div className="signin-link-success">
        <h3>{heading}</h3>
        {emailSent === true && (
          <p>
            <strong>{email}</strong> has the onboarding email: the button, their login ID,
            the steps with pictures, and the quick start guide. Nothing more to send. The
            link is below in case they ask for it another way.
          </p>
        )}
        {emailSent === false && (
          <p className="error-text">
            The email did not go out (email isn't set up on this deployment, or it was
            switched off). Send the link below yourself.
          </p>
        )}
        <p>
          {firebaseUserCreated
            ? (isGoogle
                ? <>Send this setup link to <strong>{email}</strong>. When they click it they
                    choose their password on our page (scrambled in their browser; we never see it),
                    then sign in on Google's page as{identityEmail ? <> <span className="mono">{identityEmail}</span></> : ' their login ID'} with
                    that password and set up 2-Step Verification.</>
                : <>Send this password-setup link to <strong>{email}</strong>. They click it,
                    set a password on the hosted page, and land on the Portal login
                    ready to sign in. From then on they bookmark the Portal and use email +
                    password normally.</>)
            : (hasPassword
                ? <>This link will <strong>reset</strong> {email}'s existing password.
                    Use it only if they actually asked. To just add them to the org,
                    cancel this and re-invite — we'll skip the link automatically.</>
                : <><strong>{email}</strong> has an account but hasn't finished setup.
                    This setup link lets them complete it.</>)}
        </p>
      </div>

      <div className="signin-link-row">
        <input
          id="setup-link-input"
          className="form-input signin-link-input"
          readOnly
          value={setupLink}
          onFocus={e => e.target.select()}
        />
        <button type="button" className="btn btn-primary" onClick={() => copy(setupLink, 'setup-link-input')}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>

      <p className="signin-link-hint">
        {hasPassword
          ? <>Single-use, and it expires in about an hour — have them use it
              promptly. If it lapses, generate another from the Members tab.</>
          : <>Paste into email, Slack, or screen-share. If they lose it, use
              <strong> Resend link</strong> on the Members tab.</>}
      </p>

      <div className="button-row">
        <button type="button" className="btn btn-ghost" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}
